/**
 * Режим терминала сбора данных.
 *
 * Терминал открывает ту же систему, что и компьютер, но экран у него 4 дюйма,
 * а код прилетает не с клавиатуры, а от встроенного сканера. Здесь мы один раз
 * определяем «мы на терминале» и включаем компактную вёрстку; страницам ничего
 * знать не нужно — они работают как прежде.
 */
import { useEffect, useState } from 'react';

const UA_MARK = 'FullFixTSD';

export function isTsdDevice() {
  if (typeof navigator === 'undefined') return false;
  if (navigator.userAgent.includes(UA_MARK)) return true;
  // Открыли в обычном браузере терминала — узкий экран с сенсором тоже считаем ТСД
  return window.matchMedia('(max-width: 520px) and (pointer: coarse)').matches;
}

/** Включает класс на body — им пользуется tsd.css. */
export function applyTsdMode() {
  const on = isTsdDevice();
  document.body.classList.toggle('tsd', on);
  // Настоящий терминал (наше приложение) отличаем от телефона клиента: оба узкие и
  // сенсорные, и оба получают .tsd, но кое-что нужно только телефону (кнопка «?»).
  document.body.classList.toggle('tsd-app', typeof navigator !== 'undefined'
    && navigator.userAgent.includes(UA_MARK));
  if (on) document.documentElement.setAttribute('data-tsd', '1');
  return on;
}

/**
 * Приём кода со сканера терминала.
 *
 * Приложение терминала само кладёт код в активное поле — существующие экраны
 * сканирования работают без правок. Но если экрану нужен код напрямую (нет поля
 * ввода, или надо перехватить до подстановки), он подписывается этим хуком:
 * вызов preventDefault в обработчике отменяет подстановку в поле.
 */
export function useScanner(onScan, enabled = true) {
  useEffect(() => {
    if (!enabled || typeof onScan !== 'function') return undefined;
    const handler = (e) => {
      const code = (e.detail?.code || '').trim();
      if (!code) return;
      const handled = onScan(code);
      if (handled !== false) e.preventDefault();   // мы обработали — в поле не подставлять
    };
    window.addEventListener('tsd-scan', handler);
    return () => window.removeEventListener('tsd-scan', handler);
  }, [onScan, enabled]);
}

/** Короткая вибрация — на складе шумно, звук слышно не всегда. */
export function tsdFeedback(ok = true) {
  try {
    if (window.TSDNative?.vibrate) window.TSDNative.vibrate(ok ? 40 : 200);
    else if (navigator.vibrate) navigator.vibrate(ok ? 40 : 200);
  } catch { /* вибро — не критичная функция */ }
}

export function useTsdMode() {
  const [tsd, setTsd] = useState(() => isTsdDevice());
  useEffect(() => {
    setTsd(applyTsdMode());
    const mq = window.matchMedia('(max-width: 520px)');
    const onChange = () => setTsd(applyTsdMode());
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return tsd;
}
